export type TimeseriesSide = "left" | "right"

/** A y axis series can be plotted against, named by id. */
export type TimeseriesAxis = {
  id: string
  side: TimeseriesSide
  /**
   * Fix the ends of the axis's range. An end left open fits every series on the axis, shown
   * or not, so narrowing the legend leaves the scale where it was.
   */
  min?: number
  max?: number
  /**
   * Where on the plot the axis's data should sit, as fractions of its height from the bottom,
   * so series on different axes keep to their own part of the plot. With an end fixed, the
   * middle of the data is put at the middle of the band; nothing is ever pushed off the plot.
   */
  band?: [number, number]
  /** Formats the axis's values, on the axis and in the tooltip. */
  format?: (value: number) => string
  /** Colours the axis's labels; by default, the colour of its series where it has only one. */
  color?: string
}

/** One value at a time in epoch milliseconds; null marks a gap the line breaks across. */
export type TimeseriesPoint = {
  time: number
  value: number | null
}

export type TimeseriesSeries = {
  name: string
  points: TimeseriesPoint[]
  /** The id of the axis the series is plotted against; defaults to the first axis. */
  axis?: string
  /** Defaults to the next colour in the palette. */
  color?: string
}

// A flat line's domain is widened by this fraction of its value, or by 1 at zero.
const FLAT_PADDING = 0.1

/** The lowest and highest value the given series plot on an axis, or null with none. */
export function axisDomain(series: Array<TimeseriesSeries & { axis: string }>, axis: string): [number, number] | null {
  let min = Infinity
  let max = -Infinity

  for (const s of series) {
    if (s.axis !== axis) continue

    for (const p of s.points) {
      if (p.value === null) continue

      min = Math.min(min, p.value)
      max = Math.max(max, p.value)
    }
  }

  if (min === Infinity) {
    return null
  }

  if (min === max) {
    const pad = Math.abs(min) * FLAT_PADDING || 1
    return [min - pad, max + pad]
  }

  return [min, max]
}

/** The values with no value beside them on either side, which a line cannot draw. */
export function isolatedPoints(points: TimeseriesPoint[]): TimeseriesPoint[] {
  return points.filter(
    (p, i) => p.value !== null && (points[i - 1]?.value ?? null) === null && (points[i + 1]?.value ?? null) === null,
  )
}

/**
 * Each axis's place on its side, by id: 0 beside the plot, counting outward in the order the
 * axes are given.
 */
export function axisSlots(axes: TimeseriesAxis[]): Record<string, number> {
  const counts: Record<TimeseriesSide, number> = { left: 0, right: 0 }
  const result: Record<string, number> = {}

  for (const axis of axes) {
    result[axis.id] = counts[axis.side]++
  }

  return result
}

/** Every time any series has a point at, ascending and without repeats. */
export function timesOf(series: TimeseriesSeries[]): number[] {
  const times = new Set<number>()
  for (const s of series) {
    for (const p of s.points) {
      times.add(p.time)
    }
  }

  return [...times].sort((a, b) => a - b)
}

/** The index of the time closest to t in ascending times, or null with none. */
export function nearestIndex(times: number[], t: number): number | null {
  if (times.length === 0) {
    return null
  }

  let lo = 0
  let hi = times.length - 1
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2)
    if (times[mid] < t) {
      lo = mid + 1
    } else {
      hi = mid
    }
  }

  if (lo > 0 && t - times[lo - 1] <= times[lo] - t) {
    return lo - 1
  }

  return lo
}

/**
 * Whether a series is shown under the legend's selection: every series while none is
 * selected, and only the selected ones once any is.
 */
export function isShown(selected: string[], name: string): boolean {
  return selected.length === 0 || selected.includes(name)
}

/** The selection after a legend click on a series: added if it was out, removed if it was in. */
export function toggleSelected(selected: string[], name: string): string[] {
  return selected.includes(name) ? selected.filter((n) => n !== name) : [...selected, name]
}

/**
 * The first of each month from start to end inclusive, in epoch milliseconds, taking every
 * nth month where more than max would fall in the span.
 */
export function monthStarts(start: number, end: number, max: number = 8): number[] {
  const from = new Date(start)
  const year = from.getUTCFullYear()
  let month = from.getUTCMonth()
  if (from.getTime() > Date.UTC(year, month, 1)) {
    month++
  }

  const all: number[] = []
  for (let t = Date.UTC(year, month, 1); t <= end; t = Date.UTC(year, ++month, 1)) {
    all.push(t)
  }

  const step = Math.max(1, Math.ceil(all.length / max))

  return all.filter((_, i) => i % step === 0)
}

/** The axis's range: its fixed ends, and the data's where it leaves one open. */
export function resolveDomain(axis: TimeseriesAxis, data: [number, number] | null): [number, number] | null {
  if (axis.min !== undefined && axis.max !== undefined) {
    return [axis.min, axis.max]
  }
  if (data === null) {
    return null
  }
  if (axis.band !== undefined) {
    return bandDomain(data, axis.band, axis.min, axis.max)
  }

  return [axis.min ?? data[0], axis.max ?? data[1]]
}

// bandDomain places data within band, holding at most one fixed end, and widening the range
// to keep every value on the plot.
function bandDomain(
  [lo, hi]: [number, number],
  [bottom, top]: [number, number],
  min: number | undefined,
  max: number | undefined,
): [number, number] {
  const mid = (lo + hi) / 2
  const centre = (bottom + top) / 2

  if (min !== undefined) {
    const fitted = mid > min && centre > 0 ? min + (mid - min) / centre : hi
    return [min, Math.max(fitted, hi)]
  }

  if (max !== undefined) {
    const fitted = mid < max && centre < 1 ? max - (max - mid) / (1 - centre) : lo
    return [Math.min(fitted, lo), max]
  }

  const span = (hi - lo) / (top - bottom)
  const start = lo - bottom * span

  return [start, start + span]
}

/**
 * The span of time the x axis covers: from the first time to the last, reaching back from the
 * last where that is shorter than minSpan, so a short history sits at the right rather than
 * stretching across. Null with no times.
 */
export function timeDomain(times: number[], minSpan: number): [number, number] | null {
  if (times.length === 0) {
    return null
  }

  const last = times[times.length - 1]

  return [Math.min(times[0], last - minSpan), last]
}
